/**
 * Value Estimator LLM service
 *
 * Given one item, one attribute, the scale in effect, and the evidence the
 * runner gathered (precedents from this workspace, reference documents), asks
 * the model for exactly one of two things: a value on the scale with a
 * confidence and a rationale that cites evidence ids, or an abstain that names
 * the missing input.
 *
 * Registry: value_estimator — workspace agent. Transport: callAgentLlm
 * (honors the workspace model and prompt overlay; throws AgentLlmError when no
 * provider is reachable). PRD: docs/9-exploration/PRD-value-suggestion-agent.md §4.
 */

import { AgentLlmError, callAgentLlm } from './llmClient';
import {
  abstainBeforeCall,
  buildEstimatePrompt,
  buildEstimateSystemPrompt,
  parseEstimateResponse,
  type EstimatePromptInput,
  type EstimateResult,
  type ModelParams,
} from '../../supabase/functions/_shared/estimateValueCore.ts';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

// The prompt, the parse and the evidence shapes are the behavior's shared core
// (one copy with the server runtime).
export {
  buildEstimatePrompt,
  parseEstimateResponse,
  CONFIDENCE_SCORES,
  type EstimatePrecedent,
  type EstimateReferenceDoc,
  type EstimateScalePoint,
  type EstimateConfidence,
  type EstimateResult,
} from '../../supabase/functions/_shared/estimateValueCore.ts';

/** A run's input: the prompt input plus how to call the model. */
export interface EstimateInput extends EstimatePromptInput {
  model?: string;
  /** The workspace overlay (agent_settings.system_prompt) — it specializes, never replaces. */
  systemPrompt?: string | null;
  modelParams?: ModelParams;
  /** Per-call timeout from the agent's limits (spec §2 K). */
  timeoutMs?: number;
}

// ── Run ───────────────────────────────────────────────────────────────────────

/**
 * Ask the model. The system prompt is the full instruction stack with the
 * workspace text as a subordinate overlay — it can specialize, never replace
 * the behavior's contract (spec §2 H). Throws {@link AgentLlmError} when no
 * provider is reachable; the runner records that as an error, never as an
 * abstain. Metering happens in the runner, before this is called.
 */
export async function runValueEstimator(input: EstimateInput): Promise<EstimateResult> {
  const early = abstainBeforeCall(input);
  if (early) return early;
  const params = input.modelParams;
  const { systemPrompt } = buildEstimateSystemPrompt({ overlay: input.systemPrompt, language: params?.language ?? null });
  const raw = await callAgentLlm({
    userPrompt: buildEstimatePrompt(input),
    systemPrompt,
    model: input.model ?? DEFAULT_MODEL,
    conversationId: `value-estimator-${input.item.id}-${input.attribute.key}`,
    temperature: params?.temperature ?? 0.2,
    maxTokens: params?.maxTokens ?? 500,
    timeoutMs: input.timeoutMs,
  });
  if (!raw) throw new AgentLlmError('The model returned an empty reply.');
  return parseEstimateResponse(raw, input);
}
